import { makeAutoObservable, runInAction } from 'mobx'
import {
	fetchNotificationPreferences,
	patchNotificationPreferences,
} from 'api/notificationPreferences'
import {
	DEFAULT_NOTIFICATION_PREFERENCES,
	NOTIFICATION_PREFERENCE_KEYS,
} from 'shared/config/typeNotifications'

const getApiErrorMessage = (error, fallback) =>
	error?.response?.data?.error || error?.message || fallback

class NotificationPreferencesStore {
	preferences = { ...DEFAULT_NOTIFICATION_PREFERENCES }
	loadStatus = 'idle'
	saveStatus = 'idle'
	error = null
	_inflight = null

	constructor() {
		makeAutoObservable(this, { _inflight: false }, { autoBind: true })
	}

	isEnabled(key) {
		return this.preferences[key] !== false
	}

	async ensureLoaded({ force = false } = {}) {
		if (!force && this.loadStatus === 'success') {
			return this.preferences
		}
		if (this._inflight) {
			return this._inflight
		}

		this.loadStatus = 'loading'
		this.error = null

		this._inflight = (async () => {
			try {
				const response = await fetchNotificationPreferences()
				const data = response?.data || {}
				const next = { ...DEFAULT_NOTIFICATION_PREFERENCES }
				for (const key of NOTIFICATION_PREFERENCE_KEYS) {
					if (typeof data[key] === 'boolean') {
						next[key] = data[key]
					}
				}
				runInAction(() => {
					this.preferences = next
					this.loadStatus = 'success'
					this.error = null
				})
				return next
			} catch (error) {
				runInAction(() => {
					this.loadStatus = 'error'
					this.error = getApiErrorMessage(
						error,
						'Failed to load notification preferences',
					)
				})
				throw error
			} finally {
				this._inflight = null
			}
		})()

		return this._inflight
	}

	async updatePreference(key, enabled) {
		if (!NOTIFICATION_PREFERENCE_KEYS.includes(key)) {
			throw new Error(`Unknown notification preference: ${key}`)
		}

		const previous = this.preferences[key]
		runInAction(() => {
			this.preferences = { ...this.preferences, [key]: enabled }
			this.saveStatus = 'saving'
			this.error = null
		})

		try {
			const response = await patchNotificationPreferences({ [key]: enabled })
			const data = response?.data || {}
			runInAction(() => {
				const next = { ...this.preferences }
				for (const prefKey of NOTIFICATION_PREFERENCE_KEYS) {
					if (typeof data[prefKey] === 'boolean') {
						next[prefKey] = data[prefKey]
					}
				}
				this.preferences = next
				this.saveStatus = 'success'
			})
			return this.preferences
		} catch (error) {
			runInAction(() => {
				this.preferences = { ...this.preferences, [key]: previous }
				this.saveStatus = 'error'
				this.error = getApiErrorMessage(
					error,
					'Failed to save notification preference',
				)
			})
			throw error
		}
	}
}

export const notificationPreferencesStore = new NotificationPreferencesStore()
